/**
 * `api-client.ts` needs to attach `Authorization: Bearer <token>` to every authenticated
 * request, but it is a plain module, not a React component, so it cannot call the
 * `useGetAccessToken()` hook directly. `AccessTokenBridge` (mounted once, inside
 * `CdpProvider`) registers the hook's `getAccessToken` function here so the fetch layer
 * can call it. This is the only piece of cross-wiring between the adapter and the API client.
 *
 * The token itself is never cached or logged here — every call goes back to the CDP SDK,
 * which manages refresh/expiry internally.
 */
type GetAccessToken = () => Promise<string | null>;

let currentGetAccessToken: GetAccessToken | null = null;

export function registerAccessTokenGetter(fn: GetAccessToken | null): void {
  currentGetAccessToken = fn;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One quick, silent retry after a getter failure/empty result before giving up. This exists for
 * a specific, observed window: right after a wallet action (`release`, `openDispute`,
 * `claimAfterDeadline` — see useEscrowAction.ts) confirms onchain, callers commonly
 * `invalidateQueries` immediately, which refetches while the CDP SDK is still settling from the
 * signing flow — `getAccessToken()` can transiently throw or resolve to nothing in that window.
 * Without this, that refetch goes out with no `Authorization` header at all, our own API
 * correctly 401s "missing bearer token", and the caller has no way to distinguish that from a
 * real signed-out session. A single ~300ms-delayed retry is cheap (only ever paid on the
 * already-rare failure path) and resolves the transient case; a genuinely signed-out session
 * still correctly resolves to null after the retry.
 */
export async function getBearerToken(): Promise<string | null> {
  if (!currentGetAccessToken) return null;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const token = await currentGetAccessToken();
      if (token) return token;
    } catch {
      // fall through to retry/give-up below
    }
    if (attempt === 0) await sleep(300);
  }
  return null;
}
